
import { useState, useEffect } from 'react';
import { GlobalFlagsProps } from './types';

/**
 * Hook to manage global flags related to level progression
 * This ensures critical flags like recycled words tracking are accessible across components
 */
export const useGlobalFlags = ({ currentLevelId }: GlobalFlagsProps) => {
  // Initialize global flags if they don't exist
  useEffect(() => {
    // Define all global flags used by the level system if not already defined
    if (typeof (window as any).__HAD_RECYCLED_WORDS__ === 'undefined') {
      (window as any).__HAD_RECYCLED_WORDS__ = false;
      console.debug('🚩 Initializing global flag: __HAD_RECYCLED_WORDS__ = false');
    }
    
    if (typeof (window as any).__PERFECT_LEVEL_STATUS__ === 'undefined') {
      (window as any).__PERFECT_LEVEL_STATUS__ = true;
      console.debug('🚩 Initializing global flag: __PERFECT_LEVEL_STATUS__ = true');
    }
    
    if (typeof (window as any).__LEVEL_SKIPPED__ === 'undefined') {
      (window as any).__LEVEL_SKIPPED__ = false;
      console.debug('🚩 Initializing global flag: __LEVEL_SKIPPED__ = false');
    }
  }, []);
  
  // Check localStorage for level-specific flags
  useEffect(() => {
    if (!currentLevelId) return;
    
    try {
      // Check for recycled words flag in localStorage
      const storedRecycledFlag = localStorage.getItem(`level_${currentLevelId}_had_recycled_words`);
      if (storedRecycledFlag === 'true' && !(window as any).__HAD_RECYCLED_WORDS__) {
        console.debug(`🔍 Found recycled flag in localStorage for level ${currentLevelId}, updating global flag`);
        (window as any).__HAD_RECYCLED_WORDS__ = true;
      }
    } catch (e) {
      console.error('Error checking localStorage for level flags:', e);
    }
  }, [currentLevelId]);
  
  // Method to reset global flags for a new level
  const resetLevelFlags = (newLevelId?: string) => {
    console.debug('🚩 [useGlobalFlags] MAJOR FLAG RESET for level progression');
    
    // STEP 1: Reset ALL global window flags
    console.debug('🚩 [useGlobalFlags] Resetting global window flags');
    (window as any).__HAD_RECYCLED_WORDS__ = false;
    (window as any).__PERFECT_LEVEL_STATUS__ = true;
    (window as any).__MATCH_EXERCISE_HAD_MISTAKE__ = false;
    (window as any).__MATCH_EXERCISE_IS_PERFECT__ = true;
    (window as any).__SPELLING_EXERCISE_HAD_MISTAKE__ = false;
    (window as any).__FLASHCARD_EXERCISE_HAD_MISTAKE__ = false; // Add FlashCard flag reset
    (window as any).__LEVEL_SKIPPED__ = false;
    
    // Log all flag resets for debugging
    console.log('🧹 [useGlobalFlags] Complete state of global window flags after reset:');
    console.log(`- __HAD_RECYCLED_WORDS__: ${(window as any).__HAD_RECYCLED_WORDS__}`);
    console.log(`- __MATCH_EXERCISE_HAD_MISTAKE__: ${(window as any).__MATCH_EXERCISE_HAD_MISTAKE__}`);
    console.log(`- __SPELLING_EXERCISE_HAD_MISTAKE__: ${(window as any).__SPELLING_EXERCISE_HAD_MISTAKE__}`);
    console.log(`- __FLASHCARD_EXERCISE_HAD_MISTAKE__: ${(window as any).__FLASHCARD_EXERCISE_HAD_MISTAKE__}`);
    
    // STEP 2: Clear ALL global localStorage flags (important for cross-level persistence)
    console.debug('🚩 [useGlobalFlags] Removing global localStorage flags');
    try {
      // Remove ALL flags that affect perfect level status
      localStorage.removeItem('match_exercise_had_mistake');
      localStorage.removeItem('spelling_exercise_had_mistake');
      localStorage.removeItem('flashcard_exercise_had_mistake'); // Add FlashCard flag removal
      localStorage.removeItem('perfect_level_status');
      // Also remove any legacy flags that might exist
      localStorage.removeItem('had_mistake');
      localStorage.removeItem('recycled_words');
      localStorage.removeItem('had_recycled_words');
      
      // For debugging purposes, log the current value after removal
      console.debug(`🚩 [useGlobalFlags] After removal, match_exercise_had_mistake = ${localStorage.getItem('match_exercise_had_mistake')}`);
    } catch (e) {
      console.error('[useGlobalFlags] Error clearing global localStorage flags:', e);
    }
    
    // STEP 3: Clean up old level-specific localStorage values
    if (currentLevelId) {
      console.debug(`🚩 [useGlobalFlags] Cleaning up flags for level ${currentLevelId}`);
      try {
        // Clean up ALL possible flags for the previous level
        localStorage.removeItem(`level_${currentLevelId}_had_recycled_words`);
        localStorage.removeItem(`match_exercise_had_mistake_level_${currentLevelId}`);
        localStorage.removeItem(`spelling_exercise_had_mistake_level_${currentLevelId}`);
        localStorage.removeItem(`flashcard_exercise_had_mistake_level_${currentLevelId}`); // Add FlashCard level flag removal
        localStorage.removeItem(`level_${currentLevelId}_perfect_status`);
        
        // For debugging, verify the flags were cleared
        console.debug(`🧹 [useGlobalFlags] After cleanup, match_exercise_had_mistake_level_${currentLevelId} = ${localStorage.getItem(`match_exercise_had_mistake_level_${currentLevelId}`)}`);
        console.debug(`🧹 [useGlobalFlags] All localStorage flags cleared for level ${currentLevelId}`);
      } catch (e) {
        console.error('[useGlobalFlags] Error cleaning localStorage:', e);
      }
    }
    
    // STEP 4: Initialize localStorage for the new level if provided
    if (newLevelId) {
      console.debug(`🔐 [useGlobalFlags] Initializing fresh flags for new level: ${newLevelId}`);
      try {
        // Set ALL level-specific flags to their initial perfect state
        localStorage.setItem(`level_${newLevelId}_had_recycled_words`, 'false');
        localStorage.setItem(`match_exercise_had_mistake_level_${newLevelId}`, 'false');
        localStorage.setItem(`spelling_exercise_had_mistake_level_${newLevelId}`, 'false');
        localStorage.setItem(`flashcard_exercise_had_mistake_level_${newLevelId}`, 'false'); // Add FlashCard level flag init
        localStorage.setItem(`level_${newLevelId}_perfect_status`, 'true');
        
        // Also explicitly set global flags for this level
        localStorage.setItem('match_exercise_had_mistake', 'false');
        localStorage.setItem('spelling_exercise_had_mistake', 'false');
        localStorage.setItem('flashcard_exercise_had_mistake', 'false'); // Add FlashCard global flag init
        localStorage.setItem('perfect_level_status', 'true');
        
        // Verify the new flags are set correctly for debugging
        console.debug(`🔐 [useGlobalFlags] After init, match_exercise_had_mistake = ${localStorage.getItem('match_exercise_had_mistake')}`);
        console.debug(`🔐 [useGlobalFlags] After init, match_exercise_had_mistake_level_${newLevelId} = ${localStorage.getItem(`match_exercise_had_mistake_level_${newLevelId}`)}`);
        console.debug(`🔐 [useGlobalFlags] After init, flashcard_exercise_had_mistake = ${localStorage.getItem('flashcard_exercise_had_mistake')}`);
        console.debug(`🔐 [useGlobalFlags] After init, flashcard_exercise_had_mistake_level_${newLevelId} = ${localStorage.getItem(`flashcard_exercise_had_mistake_level_${newLevelId}`)}`);
        console.debug(`🔐 [useGlobalFlags] All flags initialized for new level: ${newLevelId}`);
      } catch (e) {
        console.error('[useGlobalFlags] Error setting localStorage:', e);
      }
    }
  };
  
  // Method to mark the level as having recycled words (imperfect)
  const markHadRecycledWords = (levelId?: string) => {
    console.debug('⚠️ Marking level as having recycled words');
    
    // Set the global flag
    (window as any).__HAD_RECYCLED_WORDS__ = true;
    
    // Also store in localStorage for persistence if we have a level ID
    if (levelId) {
      try {
        localStorage.setItem(`level_${levelId}_had_recycled_words`, 'true');
        console.debug(`🔐 Saved recycled words status to localStorage for level ${levelId}`);
      } catch (e) {
        console.error('Error saving recycled status to localStorage:', e);
      }
    }
  };
  
  // Method to set the perfect level status flag
  const setPerfectLevelStatus = (isPerfect: boolean) => {
    console.debug(`⭐️ Setting global perfect level status: ${isPerfect}`);
    (window as any).__PERFECT_LEVEL_STATUS__ = isPerfect;
  };
  
  // Method to get the current value of had recycled words
  const getHadRecycledWords = (): boolean => {
    return (window as any).__HAD_RECYCLED_WORDS__ === true;
  };
  
  // Method to get the current perfect level status
  const getPerfectLevelStatus = (): boolean => {
    return (window as any).__PERFECT_LEVEL_STATUS__ === true;
  };
  
  return {
    resetLevelFlags,
    markHadRecycledWords,
    setPerfectLevelStatus,
    getHadRecycledWords,
    getPerfectLevelStatus
  };
};
